import { cloneElement, isValidElement, ReactElement, ReactNode } from 'react'
import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import { EUI_EMPTY_LABEL_SPACE, EUI_FORM_MAX_WIDTH } from '../euiParity'
import { FormRowProps } from './types'

/** EUI accepts a node or a list of nodes for `error`/`helpText`; normalize to a list. */
function toMessages( value: FormRowProps['error'] ): ReactNode[] {
	if ( value === undefined || value === null || value === false ) {
		return []
	}

	return Array.isArray( value ) ? value : [ value ]
}

/**
 * `display` controls where the control sits within the row. Only `center`
 * appears in the app today; the rest are mapped so the prop stays honest
 * rather than silently ignored. There's no `.MuiFormLabel-root` to target
 * here the way the old static-label branch had — the label now lives inside
 * the control itself (see below).
 */
function layoutSx( display: FormRowProps['display'] ): SxProps<Theme> {
	if ( display === 'center' || display === 'centerCompressed' ) {
		return { display: 'flex', justifyContent: 'center' }
	}

	if ( display === 'row' || display === 'rowCompressed' ) {
		return { display: 'flex', flexDirection: 'row', alignItems: 'center', columnGap: 1 }
	}

	return {}
}

/**
 * Floating labels (SC2, 2026-09-04 — see docs/ds2-to-mui-ux-design-plan.md's
 * "Open UX questions") live inside the control itself via `TextField`'s own
 * `label` prop, not in a row-owned `FormLabel`. `TextField` — and anything
 * that forwards unknown props to a `TextField`-shaped `inputComponent` /
 * `customInput`, like `PhoneInputWithCountry` or `NumericFormat` — already
 * renders label + helper text + error state internally once it has one, so
 * this row has no visual wrapper of its own: it clones its single child,
 * injecting the label/error/helperText callers already pass to the row.
 *
 * This means the MUI branch's `children` must be a control that understands
 * `label`/`error`/`helperText` (a `TextField`, or something that forwards to
 * one) — unlike the EUI branch, which works with any child, because real
 * `EuiFormRow` renders its own label regardless of what's inside it.
 *
 * `className`/`style` are the exception to that injection: `EuiFormRow` puts
 * both on the *row*, so they go on this wrapper, not into the clone (sc-567859).
 *
 * Knowingly unsupported `EuiFormRowProps` (all unused by any call site — check
 * before adding a consumer that needs one): `describedByIds` and `hasChildLabel`
 * both describe label↔control wiring that `TextField` owns internally here, and
 * `css`/`data-test-subj` are EUI-specific plumbing.
 */
export default function FormRowMui( {
	label,
	labelAppend,
	helpText,
	error,
	isInvalid,
	isDisabled,
	fullWidth,
	display,
	hasEmptyLabelSpace,
	children,
	id,
	className,
	style,
	// A deliberate no-op rather than an oversight (sc-567859). EUI's `'legend'`
	// makes the row a `<fieldset>` regardless of label; all 6 call sites pair it
	// with `label: ''`, so that's an unnamed group AT doesn't announce — the
	// column header names the control. This row stays a `<div>` on purpose.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	labelType,
}: FormRowProps ) {
	if ( !isValidElement( children ) ) {
		return children
	}

	const childProps = children.props as Record<string, unknown>

	// EUI shows help text and errors together, help first.
	const messages = [
		...toMessages( helpText ),
		...( isInvalid ? toMessages( error ) : [] ),
	]

	// `children`'s type (`ReactElement`, from the EUI-shaped `FormRowProps`) has
	// no props shape to check the injected keys against — the MUI branch's real
	// contract (a control that understands `label`/`error`/`helperText`) is
	// documented above, not something TS can verify here.
	const control = cloneElement( children as ReactElement<Record<string, unknown>>, {
		label: label !== undefined ? <>{label}{labelAppend}</> : childProps.label,
		error: isInvalid ?? childProps.error,
		// One `<span>` per message, each independently findable by its own text —
		// TextField's `Mui-error` class lands on the wrapping `FormHelperText`,
		// not these spans, so query it via `.closest('.MuiFormHelperText-root')`.
		helperText: messages.length
			? messages.map( ( message, i ) => (
				// eslint-disable-next-line react/no-array-index-key -- messages have no stable id
				<Box component="span" key={i} sx={{ display: 'block' }}>{message}</Box>
			) )
			: childProps.helperText,
		disabled: isDisabled ?? childProps.disabled,
		fullWidth: fullWidth ?? childProps.fullWidth,
		id: id ?? childProps.id,
	} )

	// EuiFormRow caps width unconditionally, so this wrapper must too — there is
	// no plain-label case that can skip it without breaking that parity. An
	// explicit `style.width` still wins: inline styles outrank the `sx` class,
	// and the cap is a `maxWidth`, so a narrower row is honored as EUI honors it.
	return (
		<Box
			className={className}
			style={style}
			sx={{
				maxWidth: fullWidth ? '100%' : EUI_FORM_MAX_WIDTH,
				...( hasEmptyLabelSpace ? EUI_EMPTY_LABEL_SPACE : {} ),
				...layoutSx( display ),
			}}
		>
			{control}
		</Box>
	)
}
